<template>
  <div class="row">
    <!-- Page Header -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
        <div>
          <h3 class="page-title text-white font-weight-bold mb-1">{{ $t('checkout.title') }}</h3>
          <p class="text-muted small mb-0">{{ $t('checkout.activatePackage') }}</p>
        </div>
        <div class="mt-3 mt-md-0">
          <button @click="back" class="btn btn-outline-secondary btn-sm">
            <i class="mdi mdi-arrow-left mr-1"></i>{{ $t('checkout.back') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Checkout Columns -->
    <div class="col-lg-8 grid-margin">
      <!-- Active Package Status -->
      <div class="card mb-3 border-0 shadow-sm" style="background: #191c24; border-radius: 12px; border: 1px solid #2c2e33;">
        <div class="card-body p-4">
          <div class="d-flex align-items-center mb-3">
            <div class="p-2 rounded-lg mr-3" style="background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.25);">
              <i class="mdi mdi-shield-check text-success" style="font-size: 24px;"></i>
            </div>
            <div>
              <h5 class="text-white font-weight-bold mb-0">{{ $t('checkout.activatePackage') }}</h5>
              <span class="badge badge-success px-2 py-1 mt-1">{{ $currentLocale === 'id' ? 'Perusahaan Aktif' : 'Enterprise Active' }}</span>
            </div>
          </div>
          <div class="row g-3 pt-2 border-top" style="border-color: #2c2e33;">
            <div class="col-md-6">
              <div class="p-3 rounded" style="background: #12151e; border: 1px solid #2c2e33;">
                <span class="text-muted small font-weight-bold text-uppercase d-block mb-1">{{ $t('checkout.freeTier') }}</span>
                <ul class="list-unstyled mb-0 small text-light">
                  <li class="mb-1"><i class="mdi mdi-check text-success mr-1"></i> {{ $currentLocale === 'id' ? 'Jaringan Kemitraan B2B' : 'Partner-to-Partner Network' }}</li>
                  <li><i class="mdi mdi-check text-success mr-1"></i> {{ $currentLocale === 'id' ? '5 Kursi Administrator Inti' : '5 Core Administrator Seats' }}</li>
                </ul>
              </div>
            </div>
            <div class="col-md-6">
              <div class="p-3 rounded" style="background: #12151e; border: 1px solid #2c2e33;">
                <span class="text-muted small font-weight-bold text-uppercase d-block mb-1">{{ $t('checkout.additionalTier') }}</span>
                <ul class="list-unstyled mb-0 small text-light">
                  <li class="mb-1"><i class="mdi mdi-account-multiple-plus text-info mr-1"></i> {{ $currentLocale === 'id' ? 'Tambahan Kursi Anggota Tim Fleksibel' : 'Flexible Team Member Seat Top-ups' }}</li>
                  <li><i class="mdi mdi-refresh text-warning mr-1"></i> {{ $currentLocale === 'id' ? 'Kredit Prorata saat Peningkatan Paket' : 'Proration Credits on Upgrade' }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- New Package Selector Table -->
      <div class="card border-0 shadow-sm" style="background: #191c24; border-radius: 12px; border: 1px solid #2c2e33;">
        <div class="card-body p-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="text-white font-weight-bold mb-0">
              <i class="mdi mdi-package-variant-closed text-primary mr-1"></i>
              {{ $t('checkout.newPackage') }}
            </h5>
          </div>

          <div class="table-responsive">
            <table class="table text-white align-middle">
              <thead>
                <tr class="text-muted small text-uppercase" style="border-bottom: 1px solid #2c2e33;">
                  <th>{{ $t('checkout.package') }}</th>
                  <th>{{ $t('checkout.pricePerUnit') }}</th>
                  <th style="width: 90px;">{{ $t('checkout.qty') }}</th>
                  <th style="width: 140px;">{{ $t('checkout.duration') }}</th>
                  <th class="text-end">{{ $t('checkout.totalPrice') }}</th>
                </tr>
              </thead>
              <tbody>
                <!-- Primary Package Row -->
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td>
                    <select
                      class="form-control text-white"
                      style="background: #2a3038; border-color: #2c2e33;"
                      v-model="selected"
                      @change="onChange"
                    >
                      <option value="">-- {{ $t('checkout.selectPackagePlaceholder') }} --</option>
                      <option
                        v-for="item in packages"
                        :key="item.id"
                        :value="{
                          id: item.id,
                          text: item.price_per_person_per_month,
                        }"
                      >
                        {{ item.package_name }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <span class="font-weight-bold text-info">
                      Rp {{ formatPrice(selected.text || 0) }}
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-secondary px-2 py-1">1</span>
                  </td>
                  <td>
                    <select
                      class="form-control text-white"
                      style="background: #2a3038; border-color: #2c2e33;"
                      name="duration"
                      :value="model.duration"
                      @input="update_number2"
                    >
                      <option value="1">1 {{ $t('register.monthUnit') }}</option>
                      <option value="3">3 {{ $t('register.monthsUnit') }}</option>
                      <option value="6">6 {{ $t('register.monthsUnit') }}</option>
                      <option value="9">9 {{ $t('register.monthsUnit') }}</option>
                      <option value="12" selected>12 {{ $t('register.monthsUnit') }}</option>
                    </select>
                  </td>
                  <td class="text-end font-weight-bold text-success">
                    Rp {{ formatPrice(sub_total() || 0) }}
                  </td>
                </tr>

                <!-- Additional Users Row -->
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td>
                    <div class="d-flex align-items-center">
                      <i class="mdi mdi-account-multiple-plus text-warning mr-2"></i>
                      <span>{{ $t('checkout.additionalUser') }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="text-muted">Rp {{ formatPrice(aditiuser || 0) }}</span>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      class="form-control text-white text-center"
                      style="background: #2a3038; border-color: #2c2e33; width: 75px;"
                      v-model.number="model.additional_user"
                      @input="update_number3"
                    />
                  </td>
                  <td>
                    <select
                      class="form-control text-white"
                      style="background: #2a3038; border-color: #2c2e33;"
                      name="duration"
                      :value="model.additional_user_duration"
                      @input="update_number4"
                    >
                      <option value="1">1 {{ $t('register.monthUnit') }}</option>
                      <option value="3">3 {{ $t('register.monthsUnit') }}</option>
                      <option value="6">6 {{ $t('register.monthsUnit') }}</option>
                      <option value="9">9 {{ $t('register.monthsUnit') }}</option>
                      <option value="12" selected>12 {{ $t('register.monthsUnit') }}</option>
                    </select>
                  </td>
                  <td class="text-end font-weight-bold text-success">
                    Rp {{ formatPrice(total_adition() || 0) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="border-top: 2px solid #2c2e33;">
                  <td colspan="4" class="text-end font-weight-bold text-muted text-uppercase small">
                    {{ $t('checkout.total') }}
                  </td>
                  <td class="text-end font-weight-bold text-warning h5 mb-0">
                    Rp {{ formatPrice(result() || 0) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Summary Column -->
    <div class="col-lg-4">
      <div class="card border-0 shadow-sm" style="background: #191c24; border-radius: 12px; border: 1px solid #2c2e33;">
        <div class="card-body p-4">
          <h5 class="text-white font-weight-bold mb-3 d-flex align-items-center">
            <i class="mdi mdi-receipt text-warning mr-2"></i>
            {{ $t('checkout.summary') }}
          </h5>

          <p class="text-muted small mb-3">
            {{ $t('checkout.packageSummary') }}:
            <span class="text-white font-weight-medium">
              {{ model.duration }} {{ $t('register.monthsUnit') }} + {{ model.additional_user }} {{ $t('checkout.additionalUser') }}
            </span>
          </p>

          <table class="table text-white mb-3">
            <tr style="border-color: #2c2e33;">
              <td class="text-muted">{{ $t('checkout.newBill') }}</td>
              <td class="text-end font-weight-bold">Rp {{ formatPrice(sub_total() || 0) }}</td>
            </tr>
            <tr style="border-color: #2c2e33;" v-if="total_adition() > 0">
              <td class="text-muted">{{ $t('checkout.additionalUser') }}</td>
              <td class="text-end font-weight-bold">Rp {{ formatPrice(total_adition() || 0) }}</td>
            </tr>
            <tr style="border-color: #2c2e33;">
              <td class="text-muted">{{ $t('checkout.credit') }}</td>
              <td class="text-end text-success font-weight-bold">(Rp 0)</td>
            </tr>
            <tr style="border-top: 2px solid #2c2e33;">
              <td class="font-weight-bold text-white h6 mb-0">{{ $t('checkout.total') }}</td>
              <td class="text-end font-weight-bold text-warning h5 mb-0">
                Rp {{ formatPrice(result() || 0) }}
              </td>
            </tr>
          </table>

          <!-- Voucher / Promo Code Input -->
          <div class="form-group mb-4">
            <label class="text-muted small font-weight-medium mb-1">
              {{ $t('checkout.discountCode') }}
            </label>
            <div class="input-group">
              <input
                type="text"
                class="form-control text-white font-monospace text-uppercase"
                style="background: #2a3038; border-color: #2c2e33;"
                :placeholder="$currentLocale === 'id' ? 'KODE PROMO' : 'PROMO CODE'"
                v-model="model.disc_code"
              />
              <button class="btn btn-outline-warning" type="button" @click="applyPromo">
                {{ $t('checkout.apply') }}
              </button>
            </div>
          </div>

          <!-- Checkout Action Buttons -->
          <div class="d-grid gap-2">
            <button
              class="btn btn-warning btn-lg font-weight-bold text-dark w-100 py-3 shadow-sm"
              @click="pay"
              :disabled="loading || !selected.id"
              style="border-radius: 8px;"
            >
              <div
                class="spinner-border spinner-border-sm text-dark mr-2"
                role="status"
                v-if="loading"
              >
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
              <i class="mdi mdi-lock-outline mr-1" v-if="!loading"></i>
              {{ $t('checkout.payNowBtn') }}
            </button>
            <button
              @click="back"
              class="btn btn-outline-secondary w-100 mt-2 py-2"
            >
              {{ $t('checkout.back') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoice Breakdown & Payment Method Selection Modal -->
    <div v-if="isModalOpen" class="custom-modal-backdrop">
      <div class="custom-modal-dialog">
        <div class="card border-0 shadow-lg text-white" style="background: #191c24; border: 1px solid #2c2e33; border-radius: 14px;">
          <!-- Modal Header -->
          <div class="card-header d-flex justify-content-between align-items-center p-4 border-bottom" style="border-color: #2c2e33; background: transparent;">
            <div class="d-flex align-items-center">
              <img width="120" src="../assets/images/logo-big.svg" alt="Gamiteam" class="mr-3" />
              <div>
                <h5 class="text-white font-weight-bold mb-0">{{ $currentLocale === 'id' ? 'Faktur Proforma' : 'Proforma Invoice' }}</h5>
                <span class="text-warning small font-monospace">#{{ invoice }}</span>
              </div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary text-muted" @click="closeModal">
              <i class="mdi mdi-close"></i>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="card-body p-4">
            <div class="table-responsive mb-3">
              <table class="table text-white align-middle">
                <thead>
                  <tr class="text-muted small text-uppercase" style="border-bottom: 1px solid #2c2e33;">
                    <th>No</th>
                    <th>{{ $t('common.description') }}</th>
                    <th>{{ $t('checkout.qty') }}</th>
                    <th>{{ $t('checkout.duration') }}</th>
                    <th class="text-end">{{ $t('checkout.total') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                    <td>1</td>
                    <td>{{ $currentLocale === 'id' ? 'Kuota Paket Langganan' : 'Subscription Plan Quota' }}</td>
                    <td>1</td>
                    <td>{{ duration }} {{ $t('register.monthsUnit') }}</td>
                    <td class="text-end font-weight-bold text-light">Rp {{ formatPrice(sub_total()) }}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);" v-if="additional_user > 0">
                    <td>2</td>
                    <td>{{ $currentLocale === 'id' ? 'Tambahan Kursi Anggota Tim' : 'Additional Team Member Seats' }}</td>
                    <td>{{ additional_user }}</td>
                    <td>{{ additional_user_duration }} {{ $t('register.monthsUnit') }}</td>
                    <td class="text-end font-weight-bold text-light">Rp {{ formatPrice(total_adition()) }}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);" v-if="disc_amount > 0">
                    <td colspan="4" class="text-end text-muted">{{ $currentLocale === 'id' ? 'Potongan Kode Promo' : 'Promo Voucher Discount' }}</td>
                    <td class="text-end text-success font-weight-bold">- Rp {{ formatPrice(disc_amount) }}</td>
                  </tr>
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);" v-if="remaining_package_disc > 0">
                    <td colspan="4" class="text-end text-muted">{{ $currentLocale === 'id' ? 'Kredit Prorata' : 'Proration Credit' }}</td>
                    <td class="text-end text-success font-weight-bold">- Rp {{ formatPrice(remaining_package_disc) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style="border-top: 2px solid #2c2e33;">
                    <td colspan="4" class="text-end font-weight-bold text-uppercase small text-muted">{{ $currentLocale === 'id' ? 'Total Tagihan' : 'Total Payable' }}</td>
                    <td class="text-end font-weight-bold text-warning h5 mb-0">Rp {{ formatPrice(total_semua()) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Payment Instructions Note -->
            <div class="p-3 rounded mb-3" style="background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.2);">
              <div class="text-info small font-weight-bold mb-1 d-flex align-items-center">
                <i class="mdi mdi-shield-lock-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Saluran Pembayaran Korporat Aman' : 'Secure Corporate Payment Channels' }}
              </div>
              <p class="text-muted small mb-0">
                {{ $currentLocale === 'id' ? 'Pilih pembayaran instan daring (Kartu / QRIS / Dompet Digital) atau transfer bank manual langsung ke rekening resmi PT Gamiteam Digital Nusantara.' : 'Choose between Instant Online Gateway (Cards / QRIS / E-Wallets) or Direct Manual Bank Transfer to official PT Gamiteam Digital Nusantara corporate account.' }}
              </p>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="card-footer d-flex justify-content-between align-items-center p-4 border-top" style="border-color: #2c2e33; background: transparent;">
            <button type="button" class="btn btn-secondary px-4" @click="closeModal">
              {{ $t('common.close') }}
            </button>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-info px-4 font-weight-bold" @click="payxendit">
                <i class="mdi mdi-flash mr-1"></i> {{ $currentLocale === 'id' ? 'Pembayaran Daring' : 'Online Payment' }}
              </button>
              <button type="button" class="btn btn-warning px-4 font-weight-bold text-dark ml-2" @click="openConfirmationModal">
                <i class="mdi mdi-bank-transfer mr-1"></i> {{ $currentLocale === 'id' ? 'Transfer Manual' : 'Manual Transfer' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Manual Bank Transfer Confirmation Modal -->
    <div v-if="isConfirmationModalOpen" class="custom-modal-backdrop">
      <div class="custom-modal-dialog" style="max-width: 550px;">
        <div class="card border-0 shadow-lg text-white" style="background: #191c24; border: 1px solid #2c2e33; border-radius: 14px;">
          <div class="card-header d-flex justify-content-between align-items-center p-4 border-bottom" style="border-color: #2c2e33; background: transparent;">
            <h5 class="text-white font-weight-bold mb-0">
              <i class="mdi mdi-bank-transfer text-warning mr-1"></i>
              {{ $t('checkout.transferModalTitle') }}
            </h5>
            <button type="button" class="btn btn-sm btn-outline-secondary text-muted" @click="closeConfirmationModal">
              <i class="mdi mdi-close"></i>
            </button>
          </div>
          <div class="card-body p-4 text-center">
            <p class="text-muted small mb-3">
              {{ $t('checkout.transferInstructions') }}
            </p>

            <div class="p-3 rounded mb-3 text-left" style="background: #12151e; border: 1px solid #2c2e33;">
              <span class="text-muted small font-weight-bold text-uppercase d-block mb-1">{{ $t('billing.paymentDestination') }}</span>
              <div class="h5 text-warning font-weight-bold mb-1">Bank Central Asia (BCA)</div>
              <div class="text-white font-weight-bold">PT Gamiteam Digital Nusantara</div>
              <div class="text-info font-monospace h6 mt-2 mb-0" style="letter-spacing: 1px;">
                5892 000 000
              </div>
            </div>

            <div class="d-flex justify-content-between p-2 rounded small text-muted mb-2" style="background: #161a23;">
              <span>{{ $currentLocale === 'id' ? 'Total Nominal Tagihan:' : 'Total Invoice Amount:' }}</span>
              <span class="text-warning font-weight-bold">Rp {{ formatPrice(total_semua()) }}</span>
            </div>
          </div>
          <div class="card-footer d-flex justify-content-between p-4 border-top" style="border-color: #2c2e33; background: transparent;">
            <button type="button" class="btn btn-secondary px-3" @click="closeConfirmationModal">
              {{ $t('checkout.postpone') }}
            </button>
            <button type="button" class="btn btn-warning px-4 font-weight-bold text-dark" @click="paymanual">
              <i class="mdi mdi-upload mr-1"></i> {{ $t('checkout.confirmTransfer') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "../config/config";
import store from "../store";
import Swal from "sweetalert2";

export default {
  name: "PayView",
  data() {
    return {
      price_per_person_per_month: "",
      selected: "",
      companyOptions: [],
      transaction: [],
      harga_bulan: "",
      model: {
        package_id: 0,
        duration: 12,
        additional_user: 0,
        additional_user_duration: 12,
        disc_code: "",
      },
      package_id: null,
      aditiuser: "",
      disc_amount: 0,
      total: 0,
      packages: [],
      duration: "12",
      additional_user: 0,
      additional_user_duration: "12",
      detail_transaksi: [],
      invoice: "",
      remaining_package_disc: 0,
      billing_id: 0,
      payment_proof: "",
      loading: false,
      isModalOpen: false,
      isConfirmationModalOpen: false,
    };
  },
  mounted() {
    this.getPackages();
  },
  methods: {
    onChange() {
      if (this.selected) {
        this.harga_bulan = this.selected.text;
        this.model.package_id = this.selected.id;
        this.aditiuser = this.selected.text;
      }
    },
    formatPrice(value) {
      const val = (Number(value) || 0).toFixed(0);
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    update_number2(event) {
      this.model.duration = Number(event.target.value);
    },
    update_number3(event) {
      this.model.additional_user = Number(event.target.value) || 0;
    },
    update_number4(event) {
      this.model.additional_user_duration = Number(event.target.value);
    },
    total_adition() {
      return (
        (parseInt(this.model.additional_user) || 0) *
        (parseInt(this.aditiuser) || 0) *
        (parseInt(this.model.additional_user_duration) || 1)
      );
    },
    sub_total() {
      return (parseInt(this.harga_bulan) || 0) * (parseInt(this.model.duration) || 1);
    },
    result() {
      return this.sub_total() + this.total_adition();
    },
    total_semua() {
      return Math.max(
        0,
        this.result() - (parseInt(this.disc_amount) || 0) - (parseInt(this.remaining_package_disc) || 0)
      );
    },
    getPackages() {
      const API_URL = config.API_URL;
      const token = store.state.authToken;

      axios
        .get(`${API_URL}package`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((resp) => {
          this.packages = resp.data.data || [];
          if (this.packages.length > 0) {
            const first = this.packages[0];
            this.selected = {
              id: first.id,
              text: first.price_per_person_per_month,
            };
            this.onChange();
          }
        })
        .catch((err) => {
          console.error("Error fetching packages:", err);
        });
    },
    applyPromo() {
      if (!this.model.disc_code) {
        Swal.fire({
          icon: "info",
          title: this.$currentLocale === 'en' ? "Promo Code" : "Kode Promo",
          text: this.$currentLocale === 'en' ? "Please enter a valid voucher code." : "Silakan masukkan kode voucher yang valid.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }
      Swal.fire({
        icon: "success",
        title: this.$currentLocale === 'en' ? "Code Applied" : "Kode Diterapkan",
        text: this.$currentLocale === 'en'
          ? `Voucher code "${this.model.disc_code}" will be validated during checkout.`
          : `Kode voucher "${this.model.disc_code}" akan divalidasi saat checkout.`,
        confirmButtonColor: "#f59e0b",
      });
    },
    pay() {
      if (!this.model.package_id) {
        Swal.fire({
          icon: "warning",
          title: this.$currentLocale === 'en' ? "Select Package" : "Pilih Paket",
          text: this.$currentLocale === 'en' ? "Please select a subscription package before continuing." : "Silakan pilih paket berlangganan terlebih dahulu.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      this.loading = true;
      const API_URL = config.API_URL;
      const token = store.state.authToken;

      const formData = new FormData();
      formData.append("package_id", this.model.package_id);
      formData.append("duration", this.model.duration);
      formData.append("additional_user", this.model.additional_user);
      formData.append("additional_user_duration", this.model.additional_user_duration);
      formData.append("disc_code", this.model.disc_code || "");
      formData.append("_method", "POST");

      axios
        .post(`${API_URL}billing/store`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((resp) => {
          const res = resp.data.data;
          this.detail_transaksi = res;
          this.invoice = res.billing_transaction.invoice_number;
          this.billing_id = res.billing_transaction.billing_id;
          this.disc_amount = res.billing_transaction.disc_amount || 0;
          this.remaining_package_disc = res.billing_transaction.remaining_package_disc || 0;
          this.duration = res.billing.duration;
          this.additional_user = res.billing.additional_user;
          this.additional_user_duration = res.billing.additional_user_duration;

          sessionStorage.setItem("noinvoice", res.billing_transaction.invoice_number);
          sessionStorage.setItem("amount", this.total_semua());
          sessionStorage.setItem("remaining_package_disc", this.remaining_package_disc);
          sessionStorage.setItem("billing_id", res.billing_transaction.billing_id);

          this.isModalOpen = true;
        })
        .catch((err) => {
          const errMsg = err.response?.data?.errors || err.response?.data?.message || (this.$currentLocale === 'en' ? "Failed to create invoice" : "Gagal membuat invoice");
          Swal.fire({
            icon: "error",
            title: this.$currentLocale === 'en' ? "Checkout Error" : "Kesalahan Checkout",
            text: typeof errMsg === "object" ? Object.values(errMsg).flat().join(", ") : errMsg,
            confirmButtonColor: "#f59e0b",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    payxendit() {
      const API_URL = config.API_URL;
      const token = store.state.authToken;
      const formData = new FormData();
      formData.append("billing_id", this.billing_id);
      formData.append("type", "digital");
      formData.append("_method", "POST");

      axios
        .post(`${API_URL}billing/select/payment-method`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((resp) => {
          if (resp.data.data?.payment_url) {
            window.open(resp.data.data.payment_url, "_blank");
          } else {
            Swal.fire({
              icon: "info",
              title: this.$currentLocale === 'en' ? "Payment Gateway" : "Gateway Pembayaran",
              text: this.$currentLocale === 'en' ? "Redirecting to payment gateway..." : "Mengalihkan ke gateway pembayaran...",
              confirmButtonColor: "#f59e0b",
            });
          }
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: this.$currentLocale === 'en' ? "Online Gateway Error" : "Kesalahan Gateway Online",
            text: err.response?.data?.message || (this.$currentLocale === 'en' ? "Payment gateway currently unavailable." : "Gateway pembayaran saat ini tidak tersedia."),
            confirmButtonColor: "#f59e0b",
          });
        });
    },
    paymanual() {
      const API_URL = config.API_URL;
      const token = store.state.authToken;
      const formData = new FormData();
      formData.append("billing_id", this.billing_id);
      formData.append("type", "manual");
      formData.append("_method", "POST");

      axios
        .post(`${API_URL}billing/select/payment-method`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        .then(() => {
          this.closeConfirmationModal();
          this.closeModal();
          this.$router.push({ name: "PayDetail" });
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: err.response?.data?.message || "Failed to select manual payment method.",
            confirmButtonColor: "#f59e0b",
          });
        });
    },
    back() {
      this.$router.go(-1);
    },
    closeModal() {
      this.isModalOpen = false;
    },
    openConfirmationModal() {
      this.closeModal();
      this.isConfirmationModalOpen = true;
    },
    closeConfirmationModal() {
      this.isConfirmationModalOpen = false;
    },
  },
};
</script>

<style scoped>
.custom-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.82);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
}

.custom-modal-dialog {
  width: 100%;
  max-width: 680px;
  animation: modalFadeIn 0.25s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
